import { useLanguage } from '../../context/LanguageContext';
import { t } from '../../lib/translations';
import { ENTITY_COMPATIBILITY } from '../../lib/pricingLogic';
import { BookOpen, Receipt, Library, Building2, Users, Landmark, Store, Building, Heart, School, FileCheck, FileX, Globe } from 'lucide-react';

const FORM_ICONS = {
  kpir: BookOpen,
  ryczalt: Receipt,
  pelna: Library,
};

const ENTITY_ICONS = {
  jdg: Building2,
  spolka_cywilna: Users,
  spolka_jawna: Landmark,
  spolka_partnerska: Users,
  spolka_zoo: Building,
  spolka_akcyjna: Building,
  spolka_komandytowa: Store,
  spolka_komandytowo_akcyjna: Building,
  fundacja: Heart,
  stowarzyszenie: Users,
  fundacja_rodzinna: Heart,
  psa: School,
};

const ALL_ENTITIES = [
  'jdg', 'spolka_cywilna', 'spolka_jawna', 'spolka_partnerska',
  'spolka_zoo', 'spolka_akcyjna', 'spolka_komandytowa', 'spolka_komandytowo_akcyjna',
  'fundacja', 'stowarzyszenie', 'fundacja_rodzinna', 'psa',
];

export default function Step1AccountingForm({ formData, updateField }) {
  const { language } = useLanguage();
  const forms = ['kpir', 'ryczalt', 'pelna'];

  const compatibleEntities = formData.accountingForm
    ? ENTITY_COMPATIBILITY[formData.accountingForm]
    : ALL_ENTITIES;

  const handleFormSelect = (form) => {
    updateField('accountingForm', form);
    if (formData.legalEntity && !ENTITY_COMPATIBILITY[form].includes(formData.legalEntity)) {
      updateField('legalEntity', '');
    }
  };

  const handleEntitySelect = (entity) => {
    updateField('legalEntity', entity);
    // Auto-switch to pelna if entity requires it
    if (!ENTITY_COMPATIBILITY.kpir.includes(entity) && formData.accountingForm !== 'pelna') {
      updateField('accountingForm', 'pelna');
    }
  };

  return (
    <div data-testid="step-1-accounting-form">
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A7B] mb-2">
        {t('step1.title', language)}
      </h2>
      <p className="text-slate-500 mb-8 text-base">
        {t('step1.subtitle', language)}
      </p>

      {/* Accounting Form Selection */}
      <label className="block text-sm font-medium text-slate-700 mb-3">
        {t('step1.accountingForm', language)}
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        {forms.map((form) => {
          const Icon = FORM_ICONS[form];
          const selected = formData.accountingForm === form;
          return (
            <div
              key={form}
              onClick={() => handleFormSelect(form)}
              data-testid={`accounting-form-${form}`}
              className={`selection-card cursor-pointer rounded-xl border p-5 ${
                selected ? 'selected' : 'border-slate-200 bg-white'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                selected ? 'bg-[#1B3A7B] text-white' : 'bg-slate-100 text-slate-500'
              }`}>
                <Icon className="w-5 h-5" />
              </div>
              <h3 className={`font-semibold text-sm mb-1 ${selected ? 'text-[#1B3A7B]' : 'text-slate-800'}`}>
                {t(`step1.${form}`, language)}
              </h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                {t(`step1.${form}Desc`, language)}
              </p>
            </div>
          );
        })}
      </div>

      {/* Legal Entity Selection */}
      <label className="block text-sm font-medium text-slate-700 mb-3">
        {t('step1.legalEntity', language)}
      </label>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-10">
        {ALL_ENTITIES.map((entity) => {
          const Icon = ENTITY_ICONS[entity] || Building2;
          const isCompatible = compatibleEntities.includes(entity);
          const selected = formData.legalEntity === entity;
          return (
            <div
              key={entity}
              onClick={() => isCompatible && handleEntitySelect(entity)}
              data-testid={`legal-entity-${entity}`}
              className={`rounded-lg border p-3 text-center transition-all cursor-pointer ${
                !isCompatible
                  ? 'opacity-40 cursor-not-allowed border-slate-100 bg-slate-50'
                  : selected
                  ? 'border-[#1B3A7B] bg-blue-50/40 shadow-sm ring-1 ring-[#1B3A7B]'
                  : 'border-slate-200 bg-white hover:border-[#1B3A7B] hover:shadow-sm'
              }`}
            >
              <Icon className={`w-5 h-5 mx-auto mb-1.5 ${selected ? 'text-[#1B3A7B]' : 'text-slate-400'}`} />
              <span className={`text-xs leading-tight block ${selected ? 'text-[#1B3A7B] font-medium' : 'text-slate-600'}`}>
                {t(`step1.entities.${entity}`, language)}
              </span>
            </div>
          );
        })}
      </div>

      {/* VAT Selection */}
      <label className="block text-sm font-medium text-slate-700 mb-3">
        {t('step1.vatStatus', language)}
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[true, false].map((isVat) => {
          const selected = formData.vatPayer === isVat;
          const Icon = isVat ? FileCheck : FileX;
          return (
            <div
              key={String(isVat)}
              onClick={() => updateField('vatPayer', isVat)}
              data-testid={isVat ? 'vat-yes' : 'vat-no'}
              className={`selection-card cursor-pointer rounded-xl border p-5 ${
                selected ? 'selected' : 'border-slate-200 bg-white'
              }`}
            >
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                selected ? 'bg-[#1B3A7B] text-white' : 'bg-slate-100 text-slate-500'
              }`}>
                <Icon className="w-5 h-5" />
              </div>
              <h3 className={`font-semibold text-sm mb-1 ${selected ? 'text-[#1B3A7B]' : 'text-slate-800'}`}>
                {isVat ? t('step1.vatYes', language) : t('step1.vatNo', language)}
              </h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                {isVat ? t('step1.vatYesDesc', language) : t('step1.vatNoDesc', language)}
              </p>
            </div>
          );
        })}
      </div>

      {/* VAT EU Option */}
      <div className="mt-6">
        <div
          onClick={() => updateField('vatEu', !formData.vatEu)}
          data-testid="vat-eu-toggle"
          className={`selection-card cursor-pointer rounded-xl border p-5 flex items-center gap-4 ${
            formData.vatEu ? 'selected' : 'border-slate-200 bg-white'
          }`}
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
            formData.vatEu ? 'bg-[#1B3A7B] text-white' : 'bg-slate-100 text-slate-500'
          }`}>
            <Globe className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className={`font-semibold text-sm mb-1 ${formData.vatEu ? 'text-[#1B3A7B]' : 'text-slate-800'}`}>
              {t('step1.vatEu', language)}
            </h3>
            <p className="text-xs text-slate-500 leading-relaxed">
              {t('step1.vatEuDesc', language)}
            </p>
          </div>
          <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
            formData.vatEu ? 'bg-[#1B3A7B] border-[#1B3A7B]' : 'border-slate-300'
          }`}>
            {formData.vatEu && <FileCheck className="w-3.5 h-3.5 text-white" />}
          </div>
        </div>
      </div>
    </div>
  );
}
